/**
 * actionQueueResolver — centralized read/CTA logic for persisted queue items.
 *
 * Mirrors the role of handoffRouting + handoffActions, but for the
 * persisted Action Queue. Components MUST use these selectors rather than
 * filtering items inline.
 */

import type { NavigateFunction } from "react-router-dom";
import type { ExecutableAction } from "@/lib/actionRunner";
import type { UserRole } from "@/data/RoleContext";
import type { HandoffIntent } from "@/data/opportunityTypes";
import type { ActionQueueItem } from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// Viewer
// ─────────────────────────────────────────────────────────────────────────────

export interface ActionQueueViewer {
  role: UserRole;
  userId?: string;
  label?: string;
}

/**
 * Does this queue item belong in the viewer's surface?
 *  - direct user assignment wins
 *  - else role-key assignment
 *  - unassigned items are visible to everyone (the queue is shared)
 */
function matchesViewer(item: ActionQueueItem, viewer: ActionQueueViewer): boolean {
  if (item.assignedToUserId && viewer.userId && item.assignedToUserId === viewer.userId) {
    return true;
  }
  if (item.assignedToRoleKey && item.assignedToRoleKey === viewer.role) {
    return true;
  }
  if (!item.assignedToRoleKey && !item.assignedToUserId) {
    return true; // unassigned — visible to all
  }
  return false;
}

const PRIORITY_RANK: Record<ActionQueueItem["priority"], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

/** Open + in-progress items visible to the viewer, sorted high→low priority then oldest first. */
export function getQueueItemsForViewer(
  viewer: ActionQueueViewer,
  items: ActionQueueItem[],
): ActionQueueItem[] {
  return items
    .filter((i) => i.status === "open" || i.status === "in_progress")
    .filter((i) => matchesViewer(i, viewer))
    .sort((a, b) => {
      const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
      if (p !== 0) return p;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
}

/** Count visible to the viewer — for badges. */
export function getOpenQueueCountForViewer(
  viewer: ActionQueueViewer,
  items: ActionQueueItem[],
): number {
  return getQueueItemsForViewer(viewer, items).length;
}

// ─────────────────────────────────────────────────────────────────────────────
// Execution mapping — returns ExecutableAction so consumers reuse runAction().
// ─────────────────────────────────────────────────────────────────────────────

export interface ResolveQueueActionContext {
  navigate: NavigateFunction;
  /** Optional handler invoked when the CTA's verb is "complete" — the
   *  consumer can wire it to the store's completeItem. */
  onComplete?: (id: string) => void;
}

/**
 * Map a queue item into an ExecutableAction. Defaults to a navigation to
 * the source object, or completion when no source is linked.
 */
export function resolveQueueItemAction(
  item: ActionQueueItem,
  ctx: ResolveQueueActionContext,
): ExecutableAction {
  const label = item.actionLabel ?? "Open";

  // Job-linked → navigate to the job (the universal landing surface).
  if (item.jobId) {
    return {
      label: item.actionLabel ?? "Open job",
      executionMode: "navigate",
      route: `/jobs/${item.jobId}`,
    };
  }

  if (item.issueId) {
    return {
      label: item.actionLabel ?? "Open issue",
      executionMode: "navigate",
      route: `/issues`,
    };
  }

  if (item.estimateId) {
    return {
      label: item.actionLabel ?? "Open estimate",
      executionMode: "navigate",
      route: `/opportunities/${item.estimateId}`,
    };
  }

  // Last resort — direct completion via callback.
  return {
    label,
    executionMode: "direct",
    handler: () => ctx.onComplete?.(item.id),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Lineage helper.
// ─────────────────────────────────────────────────────────────────────────────

export interface QueueItemLineage {
  /** The originating hand-off id, when this queue item was chained. */
  originHandoffId?: string;
  /** Generic source classification. */
  sourceType: ActionQueueItem["sourceType"];
  /** Linked source object id, when present. */
  sourceId?: string;
}

export function getQueueItemLineage(item: ActionQueueItem): QueueItemLineage {
  return {
    originHandoffId: item.originHandoffId,
    sourceType: item.sourceType,
    sourceId: item.sourceId ?? item.workflowSourceId,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Intent re-export — queue items use the same vocabulary as hand-offs so the
// dedupe verb mapping in workflowConvergence stays single-source.
// ─────────────────────────────────────────────────────────────────────────────

export type { HandoffIntent as ActionQueueIntent };
